<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue';
  import { SearchCombobox, FilterModal, ThemeProvider } from '@/shared/ui';
  import { positionApi, type Position } from '@/entities/Position';
  import type { TrainingMatrixDocumentPrefix } from '@/entities/Training';
  import type { TrainingMatrixFilters } from '../model/useTrainingMatrixTable';

  interface DocumentTypeOption {
    code: TrainingMatrixDocumentPrefix;
    label: string;
  }

  const DOCUMENT_TYPE_OPTIONS: DocumentTypeOption[] = [
    { code: 'MQ', label: 'Manual da Qualidade' },
    { code: 'PR', label: 'Procedimentos' },
    { code: 'IT', label: 'Instrução de Trabalho' },
    { code: 'DA', label: 'Dados' },
  ];

  const props = defineProps<{
    currentFilters?: TrainingMatrixFilters;
  }>();

  const emit = defineEmits<{
    close: [];
    'update:filter': [filters: TrainingMatrixFilters];
    'clear-filters': [];
  }>();

  const positions = ref<Position[]>([]);
  const isPositionsLoading = ref(false);
  const positionSearchQuery = ref('');

  const positionOptions = computed(() => {
    const query = positionSearchQuery.value.trim().toLowerCase();
    return positions.value
      .filter((position) => !query || position.name.toLowerCase().includes(query))
      .map((position) => ({ value: String(position.id), label: position.name }));
  });

  function handlePositionSearch(value: string) {
    positionSearchQuery.value = value;
  }

  const draftPositionId = ref('');
  const draftDocumentPrefix = ref<TrainingMatrixDocumentPrefix | undefined>(undefined);

  function loadFromFilters(filters?: TrainingMatrixFilters) {
    draftPositionId.value = filters?.positionId ? String(filters.positionId) : '';
    draftDocumentPrefix.value = filters?.documentPrefix;
  }

  async function loadPositions() {
    isPositionsLoading.value = true;
    try {
      const response = await positionApi.getAllPositions({ itemsPerPage: 1000 });
      positions.value = response.data || [];
    } catch (error) {
      console.error('Erro ao carregar cargos:', error);
    } finally {
      isPositionsLoading.value = false;
    }
  }

  onMounted(async () => {
    await loadPositions();
    loadFromFilters(props.currentFilters);
  });

  watch(
    () => props.currentFilters,
    (filters) => loadFromFilters(filters),
    { deep: true }
  );

  function toggleDocumentPrefix(code: TrainingMatrixDocumentPrefix) {
    draftDocumentPrefix.value = draftDocumentPrefix.value === code ? undefined : code;
  }

  function handleApply() {
    emit('update:filter', {
      positionId: draftPositionId.value ? Number(draftPositionId.value) : undefined,
      documentPrefix: draftDocumentPrefix.value,
    });
  }

  function handleClear() {
    draftPositionId.value = '';
    draftDocumentPrefix.value = undefined;
    positionSearchQuery.value = '';
    emit('update:filter', {});
  }
</script>

<template>
  <FilterModal
    content-class="w-auto max-w-md"
    @close="emit('close')"
    @apply-filters="handleApply"
    @clear-filters="handleClear"
  >
    <template #content>
      <ThemeProvider>
        <div class="p-3">
          <div class="p-6 space-y-6">
            <div>
              <h4 class="font-medium text-gray-07 pb-4">
                Cargo
              </h4>
              <SearchCombobox
                :model-value="draftPositionId"
                :options="positionOptions"
                :loading="isPositionsLoading"
                placeholder="Selecione um cargo"
                search-placeholder="Buscar cargo..."
                class="w-full min-w-70"
                @update:model-value="draftPositionId = $event || ''"
                @search="handlePositionSearch"
              />
            </div>
            <div>
              <h4 class="font-medium text-gray-07 pb-4">
                Documento
              </h4>
              <div
                data-testid="training-matrix-document-type-options"
                class="flex flex-col items-start gap-2"
              >
                <button
                  v-for="option in DOCUMENT_TYPE_OPTIONS"
                  :key="option.code"
                  type="button"
                  :data-testid="`training-matrix-document-type-${option.code}`"
                  class="flex items-center justify-start py-2 px-4 rounded-lg w-full"
                  :class="
                    draftDocumentPrefix === option.code
                      ? 'bg-(--theme-secondary) text-white'
                      : 'text-gray-07'
                  "
                  @click="toggleDocumentPrefix(option.code)"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
